// Meu Radar — tipos e chamadas à API (21/09/2026; pessoal desde 22/09/2026).
//
// O radar é a agenda da PESSOA: editais, negócios do pipeline e lembretes
// livres, com aviso por e-mail N dias antes do prazo, anotações e — no caso do
// edital — os aspectos que a análise da IA já levantou.
//
// 🚨 Nada aqui recalcula prazo. O servidor manda `prazo`, `alerta_em` e
// `origem_prazo` prontos. Refazer a conta no navegador é literalmente o
// defeito de 27/08/2026, que escondeu 828 editais com prazo aberto. A
// classificação visual (cor, "em N dias") é `classificarPrazoRadar`, por DIA
// de calendário, com as mesmas faixas de cor do `prazo.ts` da lista de
// oportunidades (25/09/2026: a conta em milissegundos do `prazo.ts` dizia
// "amanhã" no próprio dia do prazo).
//
// ⚠️ As actions continuam entrando por `POST /comercial/licitacoes` mesmo o
// radar tendo saído de Licitações. É proposital: rota nova no API Gateway
// exige recurso explícito + deploy do stage (docs/pegadinhas.md), o que é
// recurso novo na AWS e não cabe nesta entrega. O nome da rota é o único
// resquício — o handler é `radarPessoal.js`.
//
// 🚨 Fuso (24/09/2026): `prazo`, `prazo_manual`, `alerta_em` e
// `alerta_enviado_para_prazo` são HORA DE PAREDE de Brasília e chegam como
// texto 'YYYY-MM-DDTHH:MM:SS', sem Z nem offset. `new Date(s)`/`dayjs(s)` leem
// esse formato como hora local — o certo, para quem usa o sistema em Brasília.
// Na volta, a data sai no mesmo formato (`FORMATO_PAREDE`). Os demais
// timestamps (`criado_em`, `atualizado_em`, `alerta_enviado_em`) são UTC de
// verdade e continuam com Z. Antes, a parede saía rotulada como UTC ('…Z'): a
// tela mostrava 3 h antes e cada "Salvar" recuava o prazo 3 h.
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';

export type TipoItem = 'licitacao' | 'negocio' | 'livre';
export type TipoProposta = 'tecnica' | 'tecnica_e_preco';
export type Categoria =
  'crea' | 'marketing' | 'cliente' | 'reuniao' | 'documento' | 'financeiro' | 'outro';

/** Um aspecto relevante extraído do parecer da IA (só existe para edital). */
export interface AspectoIa { rotulo: string; texto: string }

export interface AspectosIa {
  analise_id: number;
  tipo: string | null;
  criado_em: string;
  criado_por: string | null;
  fit_score: number | null;
  resumo: string | null;
  objeto_detalhado: string | null;
  itens: AspectoIa[];
}

export interface ItemRadar {
  id: number;
  dono_email: string;
  tipo: TipoItem;
  /** Como o item se chama na tela — o servidor decide, por tipo. */
  rotulo: string;

  oportunidade_id: number | null;
  negocio_id: number | null;
  titulo: string | null;
  categoria: Categoria | null;
  tipo_proposta: TipoProposta | null;

  /** Parede BRT, 'YYYY-MM-DDTHH:MM:SS' sem offset. */
  prazo_manual: string | null;
  dias_antecedencia: number;
  alerta_email: string;
  /** UTC de verdade (gravado com NOW()), com Z. */
  alerta_enviado_em: string | null;
  /** Parede BRT — qual prazo o último aviso cobriu. */
  alerta_enviado_para_prazo: string | null;
  observacoes: string | null;

  criado_por: string | null;
  criado_em: string;
  atualizado_por: string | null;
  atualizado_em: string;

  /** Prazo efetivo — calculado no SQL. Parede BRT, sem offset. */
  prazo: string | null;
  /** Dia do aviso às 08:00 (prazo::date − antecedência) — calculado no SQL.
   *  Parede BRT, sem offset. É o ⏰ do calendário. */
  alerta_em: string | null;
  /** prazo::date − hoje (em Brasília). null sem prazo. */
  dias_restantes?: number | null;
  origem_prazo: string | null;
  aviso_pendente: boolean;

  // Edital (JOIN)
  numero_edital: string | null;
  numero_processo: string | null;
  orgao: string | null;
  uf: string | null;
  cidade: string | null;
  modalidade: string | null;
  objeto: string | null;
  valor_estimado: number | string | null;
  situacao: string | null;
  fonte: string | null;
  link_edital: string | null;
  status_interno: string | null;
  fit_score: number | null;
  area_tecnica: string | null;

  // Negócio do pipeline (JOIN)
  negocio_titulo: string | null;
  negocio_status: string | null;
  negocio_valor: number | string | null;
  negocio_responsavel: string | null;
  negocio_origem: string | null;
  negocio_cliente: string | null;

  aspectos_ia: AspectosIa | null;
}

export interface NegocioBusca {
  id: number;
  titulo: string;
  status: string;
  origem: string | null;
  valor_estimado: number | string | null;
  responsavel: string | null;
  cliente: string | null;
}

export interface ResultadoAlertas {
  sucesso: boolean;
  dry_run: boolean;
  candidatos: number;
  enviados: number;
  erros: number;
  itens: Array<{
    id: number;
    tipo: TipoItem;
    rotulo: string;
    prazo: string;
    dias_restantes: number;
    destinatarios: string[];
    assunto: string;
    com_analise: boolean;
    simulado?: boolean;
    enviado?: boolean;
    erro?: string;
  }>;
}

/** Antecedência padrão do aviso, em dias. Espelha DIAS_ANTECEDENCIA_PADRAO do
 *  handler — é só o valor inicial do formulário; quem decide é o servidor. */
export const DIAS_ANTECEDENCIA_PADRAO = 5;

/** Formato de ida e volta da hora de parede BRT (sem Z, sem offset), para
 *  `dayjs().format()`. Ver o 🚨 de fuso no topo do arquivo. */
export const FORMATO_PAREDE = 'YYYY-MM-DDTHH:mm:ss';

export const ROTULO_TIPO: Record<TipoItem, string> = {
  licitacao: 'Licitação',
  negocio: 'Negócio do pipeline',
  livre: 'Lembrete',
};

export const ROTULO_TIPO_PROPOSTA: Record<TipoProposta, string> = {
  tecnica: 'Somente proposta técnica',
  tecnica_e_preco: 'Proposta técnica e proposta de preço',
};

/** Categorias do lembrete livre. Espelha o CHECK `radar_categoria_chk` — se
 *  entrar uma nova, ela entra aqui e na migration. */
export const CATEGORIAS: Array<{ valor: Categoria; rotulo: string; emoji: string }> = [
  { valor: 'crea',       rotulo: 'CREA / atestado',     emoji: '📐' },
  { valor: 'cliente',    rotulo: 'Contato com cliente', emoji: '🤝' },
  { valor: 'reuniao',    rotulo: 'Reunião',             emoji: '📅' },
  { valor: 'marketing',  rotulo: 'Marketing',           emoji: '📣' },
  { valor: 'documento',  rotulo: 'Documento',           emoji: '📄' },
  { valor: 'financeiro', rotulo: 'Financeiro',          emoji: '💰' },
  { valor: 'outro',      rotulo: 'Outro',               emoji: '📌' },
];

// `Authorization: token` cru, sem "Bearer" — é a convenção do projeto inteiro.
async function chamar<T>(body: Record<string, unknown>): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/licitacoes`, body,
    { headers: { Authorization: token } });
  return r.data as T;
}

export interface CamposItem {
  prazo_manual?: string | null;
  dias_antecedencia?: number;
  alerta_email?: string;
  observacoes?: string | null;
  tipo_proposta?: TipoProposta | null;
  titulo?: string;
  categoria?: Categoria;
  /** 'YYYY-MM-DD' — arrastar no calendário (24/09/2026). O servidor grava o
   *  dia novo com a HORA já gravada no item, rearma o aviso e devolve o item.
   *  Recusa edital (400): o prazo dele vem da fonte. */
  mover_para_dia?: string;
}

export const radarApi = {
  listar: () => chamar<ItemRadar[]>({ action: 'radar_listar' }),

  /** Acompanhar um edital. O prazo vem da fonte — `prazo_manual` só quando a
   *  fonte não informou nada (o servidor recusa e explica). */
  acompanharLicitacao: (oportunidade_id: number, extra: CamposItem = {}) =>
    chamar<ItemRadar>({ action: 'radar_adicionar', tipo: 'licitacao', oportunidade_id, ...extra }),

  /** Acompanhar um negócio do pipeline. O pipeline não guarda data de entrega
   *  da proposta, então ela vem da pessoa. Sem `prazo_manual` (24/09/2026), o
   *  servidor põe hoje + 7 dias às 18h, com aviso no 7º dia às 8h. */
  acompanharNegocio: (negocio_id: number, extra: CamposItem) =>
    chamar<ItemRadar>({ action: 'radar_adicionar', tipo: 'negocio', negocio_id, ...extra }),

  /** Lembrete livre — título, data e categoria. */
  criarLembrete: (extra: CamposItem) =>
    chamar<ItemRadar>({ action: 'radar_adicionar', tipo: 'livre', ...extra }),

  atualizar: (id: number, campos: CamposItem) =>
    chamar<ItemRadar>({ action: 'radar_atualizar', id, ...campos }),

  remover: (id: number) => chamar<{ ok: boolean }>({ action: 'radar_remover', id }),

  buscarNegocios: (busca: string) =>
    chamar<NegocioBusca[]>({ action: 'radar_buscar_negocios', busca }),

  /** Sem `confirmar`, SIMULA — mesmo contrato do "Arquivar vencidas". */
  verificarAlertas: (confirmar = false) =>
    chamar<ResultadoAlertas>({ action: 'radar_verificar_alertas', confirmar }),
};

/** "29/09 às 8h" — dia e hora do aviso, lidos da parede BRT que o servidor
 *  mandou em `alerta_em`. null quando não há aviso. */
export function fmtDiaDoAviso(alertaEm: string | null | undefined): string | null {
  if (!alertaEm) return null;
  const d = new Date(alertaEm);
  if (Number.isNaN(d.getTime())) return null;
  // Sem anotação de tipo no corpo: o teste offline (radar-menu-comercial)
  // executa esta função tirando só a assinatura.
  const min = d.getMinutes() ? String(d.getMinutes()).padStart(2, '0') : '';
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  return `${dia}/${mes} às ${d.getHours()}h${min}`;
}

function mesmoInstante(a: string, b: string): boolean {
  const ta = new Date(a).getTime();
  return Number.isFinite(ta) && ta === new Date(b).getTime();
}

/** Texto curto do estado do aviso. `aviso_pendente` e `alerta_em` já vêm do
 *  servidor com a janela conferida — aqui só se traduz o que ele disse.
 *
 *  Regra POR DIA desde 24/09/2026 (C5): o aviso sai a partir das 8h de
 *  (dia do prazo − antecedência), em Brasília, enquanto o prazo não passou.
 *  Antes a janela era "prazo − N dias" na hora exata, contra o NOW() em UTC
 *  do banco — deslocada 3 h, e com antecedência 0 o aviso nunca saía. */
export function estadoDoAviso(item: ItemRadar): { texto: string; cor: string } {
  if (!item.prazo) return { texto: 'sem prazo', cor: '#888' };
  const prazo = new Date(item.prazo).getTime();
  const agora = Date.now();
  if (prazo < agora) return { texto: 'prazo encerrado', cor: '#888' };
  if (item.aviso_pendente) return { texto: 'aviso sai na próxima rodada', cor: '#B07500' };
  // "Enviado" só vale para ESTE prazo: se a fonte prorrogou (ou a data mudou),
  // `alerta_enviado_para_prazo` ficou no prazo antigo e o aviso está rearmado.
  // Linha sem o carimbo do prazo (anterior à coluna) conta como enviada.
  if (item.alerta_enviado_em
      && (!item.alerta_enviado_para_prazo || mesmoInstante(item.alerta_enviado_para_prazo, item.prazo))) {
    return { texto: 'aviso enviado', cor: '#0a7f4a' };
  }
  const alerta = item.alerta_em ? new Date(item.alerta_em).getTime() : NaN;
  // Prazo até as 8h (inclusive) com o aviso no próprio dia: a janela nunca
  // abre. Às 8h em ponto o alerta_em é o próprio prazo, e a rodada das 8h do
  // servidor já roda depois dele (25/09/2026: era `>`, e a tela prometia
  // "aviso às 8h" para um aviso que não sai).
  if (Number.isFinite(alerta) && alerta >= prazo) {
    return { texto: 'sem aviso — o prazo vence até as 8h do dia', cor: '#888' };
  }
  const quando = fmtDiaDoAviso(item.alerta_em);
  if (quando && alerta > agora) return { texto: `aviso em ${quando}`, cor: '#0a7f4a' };
  return { texto: 'aviso programado', cor: '#0a7f4a' };
}

/** Frase do toast do "+ Adicionar › Negócio" (25/09/2026), montada com o que
 *  o SERVIDOR gravou — e não com a promessa do formulário. Readicionar um
 *  negócio que já estava no radar mantém a data e a antecedência combinadas,
 *  então "com aviso no dia às 8h" podia ser falso nas duas partes.
 *  `encerrado` = o prazo devolvido já passou (a tela avisa em amarelo). */
export function resumoDoNegocioNoRadar(item: ItemRadar): { texto: string; encerrado: boolean } {
  const d = item && item.prazo ? new Date(item.prazo) : null;
  if (!d || Number.isNaN(d.getTime())) return { texto: 'Negócio no seu radar.', encerrado: false };
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const hora = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const quando = `${dia}/${mes} às ${hora}`;
  if (d.getTime() < Date.now()) {
    return {
      texto: `Negócio no seu radar, mas o prazo gravado (${quando}) já passou — o aviso não sai. Mude a data em Editar.`,
      encerrado: true,
    };
  }
  const estado = estadoDoAviso(item);
  const n = Number(item.dias_antecedencia) || 0;
  const antecedencia = n === 0 ? 'no dia do prazo' : n === 1 ? '1 dia antes' : `${n} dias antes`;
  const comAntecedencia = /^aviso (em|sai)/.test(estado.texto) ? ` (${antecedencia})` : '';
  return { texto: `Negócio no seu radar — entrega em ${quando}; ${estado.texto}${comAntecedencia}.`, encerrado: false };
}

/** Prazo classificado para o RADAR — cor, rótulo e ícone por DIA. */
export interface PrazoRadar {
  /** Dias de calendário até o prazo (0 = hoje; negativo = já foi). */
  dias: number;
  /** O instante do prazo já passou (mesmo que ainda seja hoje). */
  vencido: boolean;
  cor: string;
  rotulo: string;
  icone: string;
  bold: boolean;
}

/** O que `classificarPrazoRadar` lê do item. */
export type ItemComPrazo = Pick<ItemRadar, 'prazo' | 'dias_restantes'>;

/** 🚨 (25/09/2026) Classificação do prazo NO RADAR, por DIA de calendário.
 *  O `classificarPrazo` do prazo.ts (que serve Oportunidades e NÃO muda aqui)
 *  conta milissegundos com Math.ceil: no próprio dia de um prazo às 18:00,
 *  visto às 08:00, dava "amanhã"; depois das 18:00 dava "🔥 HOJE" (−0 === 0)
 *  com o prazo já encerrado; e o +7 do negócio sem data saía "em 8 dias" —
 *  contradizendo o e-mail, que conta dias no SQL.
 *
 *  Dias: `dias_restantes` do servidor quando vier número; sem ele, a mesma
 *  conta de `dayjs(prazo).startOf('day').diff(dayjs().startOf('day'), 'day')`,
 *  escrita sem dayjs para o teste offline executar a função (meia-noite a
 *  meia-noite em Date.UTC não sofre horário de verão). Prazo cujo instante já
 *  passou é "encerrado hoje" ou "vencido há Nd", nunca 🔥. Demais faixas de
 *  cor iguais às do prazo.ts. `agora` é o relógio de referência: os KPIs
 *  passam o mesmo instante para todos os itens, e o teste passa um relógio
 *  falso.
 *
 *  O `dias_restantes` é da ÚLTIMA carga: com a aba aberta de um dia para o
 *  outro, ele fica para trás (dizia "em 3 dias" no próprio dia do prazo). Por
 *  isso vale o MENOR entre ele e a conta de calendário de agora — o relógio só
 *  anda para a frente, então o menor nunca está atrasado. */
export function classificarPrazoRadar(item: ItemComPrazo, agora?: number): PrazoRadar | null {
  if (!item || !item.prazo) return null;
  const d = new Date(item.prazo);
  if (Number.isNaN(d.getTime())) return null;
  const ref = new Date(agora === undefined ? Date.now() : agora);
  const porCalendario = Math.round(
    (Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
      - Date.UTC(ref.getFullYear(), ref.getMonth(), ref.getDate())) / 86400000);
  const dias = typeof item.dias_restantes === 'number' && Number.isFinite(item.dias_restantes)
    ? Math.min(item.dias_restantes, porCalendario) : porCalendario;
  if (d.getTime() < ref.getTime()) {
    const passados = Math.min(dias, 0);
    const rotulo = passados < 0 ? `vencido há ${Math.abs(passados)}d` : 'encerrado hoje';
    return { dias: passados, vencido: true, cor: '#B91626', rotulo, icone: '⚠ ', bold: true };
  }
  if (dias <= 0) return { dias: 0, vencido: false, cor: '#B91626', rotulo: 'HOJE', icone: '🔥 ', bold: true };
  if (dias === 1) return { dias, vencido: false, cor: '#B91626', rotulo: 'amanhã', icone: '', bold: true };
  if (dias <= 3) return { dias, vencido: false, cor: '#B91626', rotulo: `em ${dias} dias`, icone: '', bold: true };
  if (dias <= 7) return { dias, vencido: false, cor: '#B07500', rotulo: `em ${dias} dias`, icone: '', bold: false };
  if (dias <= 30) return { dias, vencido: false, cor: '#0a7f4a', rotulo: `em ${dias} dias`, icone: '', bold: false };
  return { dias, vencido: false, cor: '#444', rotulo: `em ${dias} dias`, icone: '', bold: false };
}

/** Só o 🏁 de negócio e de lembrete livre se arrasta no calendário, e só para
 *  quem gerencia o próprio radar. O prazo do EDITAL vem da fonte
 *  (`prazoEfetivo()`): arrastar gravaria um `prazo_manual` que deixa de
 *  acompanhar a prorrogação — e o servidor recusa com 400 do mesmo jeito.
 *  O ⏰ nunca se arrasta: o dia do aviso é consequência do prazo. */
export function podeArrastarPrazo(item: ItemRadar, podeGerenciar: boolean): boolean {
  return podeGerenciar && item.tipo !== 'licitacao';
}

/** Emoji e rótulo por tipo/categoria — usados na agenda e no calendário. */
export function marcaDoItem(item: ItemRadar): { emoji: string; rotulo: string } {
  if (item.tipo === 'licitacao') return { emoji: '🏛️', rotulo: 'Licitação' };
  if (item.tipo === 'negocio') return { emoji: '💼', rotulo: 'Negócio' };
  const c = CATEGORIAS.find(x => x.valor === item.categoria);
  return { emoji: c?.emoji || '📌', rotulo: c?.rotulo || 'Lembrete' };
}
